// Message utility functions

import { resolveMessageAttachmentMeta } from './fileUtils';

// Convert HTML to WhatsApp markdown format
export const convertHtmlToWhatsAppMarkdown = (htmlContent) => {
  if (!htmlContent) return '';
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  const convertElementToMarkdown = (element) => {
    let result = '';
    
    for (let node of element.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        
        // Convert HTML tags to WhatsApp markdown
        if (tagName === 'b' || tagName === 'strong') {
          if (node.querySelector('i') || node.querySelector('em')) {
            // If both bold+italic are applied, send combined WhatsApp markdown
            // in canonical form: *_text_*
            const combinedText = (node.textContent || '')
              .replace(/^[_*]+|[_*]+$/g, '')
              .trim();
            result += `*_${combinedText}_*`;
          } else {
            const innerContent = convertElementToMarkdown(node);
            result += `*${innerContent}*`;
          }
        } else if (tagName === 'i' || tagName === 'em') {
          if (node.querySelector('b') || node.querySelector('strong')) {
            // If both italic+bold are applied, send combined markdown
            const combinedText = (node.textContent || '')
              .replace(/^[_*]+|[_*]+$/g, '')
              .trim();
            result += `*_${combinedText}_*`;
          } else {
            const innerContent = convertElementToMarkdown(node);
            result += `_${innerContent}_`;
          }
        } else if (tagName === 'br') {
          result += '\n';
        } else if (tagName === 'div') {
          const innerContent = convertElementToMarkdown(node);
          result += innerContent;
          // Add newline after div if it has a sibling (next or previous)
          // This preserves line breaks when pasting multi-line text
          if (node.nextSibling || (node.previousSibling && node.previousSibling.tagName === 'DIV')) {
            result += '\n';
          }
        } else if (tagName === 'p') {
          // Handle paragraph tags - add newline after content
          const innerContent = convertElementToMarkdown(node);
          result += innerContent;
          if (node.nextSibling) {
            result += '\n';
          }
        } else {
          result += convertElementToMarkdown(node);
        }
      }
    }
    
    return result;
  };
  
  const markdownText = convertElementToMarkdown(tempDiv);
  
  // Clean up extra whitespace while preserving newlines
  return markdownText
    .replace(/[ \t]+/g, ' ') // Replace multiple spaces/tabs with single space (preserve newlines)
    .replace(/\n[ \t]+/g, '\n') // Remove spaces/tabs after newlines
    .replace(/[ \t]+\n/g, '\n') // Remove spaces/tabs before newlines
    .replace(/\n{3,}/g, '\n\n') // Limit consecutive newlines to max 2
    .replace(/^\s+|\s+$/g, ''); // Trim start and end (but preserve internal newlines)
};


export const convertWhatsAppMarkdownToHtml = (markdownText) => {
  if (!markdownText) return '';
  
  // Escape HTML characters first
  let html = markdownText
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Convert WhatsApp markdown to HTML
  // Process from innermost to outermost to handle nesting correctly
  
  // First, handle nested formatting in both valid orders:
  // *_text_* and _*text*_
  html = html.replace(/\*_([^_]+)_\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/_\*([^*]+)\*_/g, '<strong><em>$1</em></strong>');
  
  // Then handle standalone bold: *text* -> <strong>text</strong>
  html = html.replace(/\*([^*]+?)\*/g, '<strong>$1</strong>');
  
  // Finally handle standalone italic: _text_ -> <em>text</em>
  html = html.replace(/_([^_]+?)_/g, '<em>$1</em>');
  
  // Handle incomplete formatting at the end of text
  html = html.replace(/\*$/, '&#42;');
  html = html.replace(/_$/, '&#95;');
  
  // Handle case where a space follows the formatting correctly
  html = html.replace(/(\*\_*[^*\n]+?\*_*)(?=\s)/g, '$1'); // Ensure no doubling up of symbols
  
  // Convert newlines to <br> tags
  html = html.replace(/\n/g, '<br>');
  
  return html;
};

// Helper function to auto-complete formatting symbols and clean up spaces
export const autoCompleteFormatting = (text) => {
  if (!text) return text;
  
  let result = text;
  
  // Normalize nested formatting to canonical WhatsApp form: *_text_*
  result = result.replace(/_\*([^*]+?)\*_/g, '*_$1_*');
  
  // Clean up spaces around formatting symbols
  // Remove spaces before closing symbols: *this is bold * -> *this is bold*
  result = result.replace(/\*([^*]+?)\s+\*/g, '*$1*');
  result = result.replace(/_([^_]+?)\s+_/g, '_$1_');
  
  // Remove spaces after opening symbols: * this is bold* -> *this is bold*
  result = result.replace(/\*\s+([^*]+?)\*/g, '*$1*');
  result = result.replace(/_\s+([^_]+?)_/g, '_$1_');

  // Ensure canonical nested formatting has no extra spaces around inner text
  result = result.replace(/\*_\s*([^_]+?)\s*_\*/g, '*_$1_*');
  result = result.replace(/_\*\s*([^*]+?)\s*\*_/g, '*_$1_*');
  
  // Handle complex cases where formatting symbols are adjacent
  // Fix cases like: *bold*and -> *bold* and
  result = result.replace(/\*([^*]+?)\*([a-zA-Z])/g, '*$1* $2');
  result = result.replace(/_([^_]+?)_([a-zA-Z])/g, '_$1_ $2');
  
  // NOTE: Do not rewrite "_*" / "*_" adjacency here.
  // Those rewrites can corrupt valid combined WhatsApp markers like "*_text_*".

  return result;
};

// Get message content from editor and convert to WhatsApp markdown
export const getMessageContent = (messageEditorRef, maxLength = 4096) => {
  if (!messageEditorRef.current) return '';
  
  // Get the HTML content
  const htmlContent = messageEditorRef.current.innerHTML;
  
  // Convert HTML to WhatsApp markdown format
  const markdown = convertHtmlToWhatsAppMarkdown(htmlContent);
  
  // Auto-complete any incomplete formatting
  const formattedMarkdown = autoCompleteFormatting(markdown);
  
  // Enforce character limit
  if (formattedMarkdown.length > maxLength) {
    return formattedMarkdown.substring(0, maxLength);
  }
  
  return formattedMarkdown;
};

// Set message content in editor, converting WhatsApp markdown to HTML
export const setMessageContent = (messageEditorRef, content) => {
  if (messageEditorRef.current) {
    // Convert WhatsApp markdown to HTML for display in editor
    const htmlContent = convertWhatsAppMarkdownToHtml(content);
    messageEditorRef.current.innerHTML = htmlContent;
  }
};

export const hasMessageContent = (messageEditorRef, attachments = []) => {
  if (!messageEditorRef.current) return false;
  
  // Get clean text content
  const textContent = getMessageContent(messageEditorRef);
  const hasText = textContent.trim() !== '';
  const hasAttachments = attachments.length > 0;
  
  return hasText || hasAttachments;
};

export const insertEmoji = (messageEditorRef, emoji) => {
  //console.log('insertEmoji called with:', emoji);
  //console.log('messageEditorRef.current:', messageEditorRef.current);
  
  if (!messageEditorRef.current) {
    console.error('messageEditorRef.current is null');
    return;
  }
  
  // Ensure the editor has focus
  messageEditorRef.current.focus();
  //console.log('Editor focused');
  
  const selection = window.getSelection();
  //console.log('Current selection:', selection);
  let range;
  
  if (selection.rangeCount > 0) {
    range = selection.getRangeAt(0);
    //console.log('Using existing range');
  } else {
    // Create a new range if none exists
    range = document.createRange();
    range.selectNodeContents(messageEditorRef.current);
    range.collapse(false); // Collapse to end
    //console.log('Created new range at end');
  }
  
  // Create text node with emoji
  const emojiNode = document.createTextNode(emoji);
  //console.log('Created emoji node:', emojiNode);
  
  try {
    // Insert the emoji
    range.insertNode(emojiNode);
    //console.log('Emoji inserted successfully');
    
    // Move cursor after the emoji - this is the key fix
    range.setStartAfter(emojiNode);
    range.setEndAfter(emojiNode);
    range.collapse(true); // Collapse to a single point after the emoji
    
    // Update selection
    selection.removeAllRanges();
    selection.addRange(range);
    //console.log('Selection updated');
    
    // Ensure focus is maintained
    messageEditorRef.current.focus();
    
    // Trigger input event to update formatting state
    const inputEvent = new Event('input', { bubbles: true });
    messageEditorRef.current.dispatchEvent(inputEvent);
    //console.log('Input event triggered');
    
    // Log the final content
    //console.log('Final editor content:', messageEditorRef.current.innerHTML);
  } catch (error) {
    console.error('Error inserting emoji:', error);
    // Fallback: simple append method with proper cursor positioning
    try {
      //console.log('Trying fallback emoji insertion...');
      const currentContent = messageEditorRef.current.innerHTML;
      messageEditorRef.current.innerHTML = currentContent + emoji;
      
      // Position cursor after the emoji in fallback method
      const newRange = document.createRange();
      const textNodes = [];
      
      // Get all text nodes
      const walker = document.createTreeWalker(
        messageEditorRef.current,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      let node;
      while (node = walker.nextNode()) {
        textNodes.push(node);
      }
      
      if (textNodes.length > 0) {
        const lastTextNode = textNodes[textNodes.length - 1];
        newRange.setStart(lastTextNode, lastTextNode.textContent.length);
        newRange.setEnd(lastTextNode, lastTextNode.textContent.length);
        newRange.collapse(true);
        
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
      
      messageEditorRef.current.focus();
      //console.log('Fallback emoji insertion successful');
    } catch (fallbackError) {
      console.error('Fallback emoji insertion also failed:', fallbackError);
    }
  }
};

// Message sorting utilities

export const sortMessagesByTime = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  
  return [...messages].sort((a, b) => {
    const timeA = new Date(a.timestamp || a.created_at || 0);
    const timeB = new Date(b.timestamp || b.created_at || 0);
    
    // Primary sort: by timestamp (oldest first)
    const timeDiff = timeA - timeB;
    
    // If times are exactly the same (within 1 second), sort by ID as tiebreaker
    if (Math.abs(timeDiff) < 1000) {
      return (a.id || 0) - (b.id || 0);
    }
    
    return timeDiff;
  });
};

export const sortMessagesByTimeDesc = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  
  return [...messages].sort((a, b) => {
    const timeA = new Date(a.timestamp || a.created_at || 0);
    const timeB = new Date(b.timestamp || b.created_at || 0);
    
    // Primary sort: by timestamp (newest first)
    const timeDiff = timeB - timeA;
    
    // If times are exactly the same (within 1 second), sort by ID as tiebreaker
    if (Math.abs(timeDiff) < 1000) {
      return (b.id || 0) - (a.id || 0);
    }
    
    return timeDiff;
  });
};

export const sortChatsByLastMessageTime = (chats) => {
  if (!Array.isArray(chats) || chats.length === 0) return [];

  return [...chats].sort((a, b) => {
    const timeA = new Date(a.updated_at || 0);
    const timeB = new Date(b.updated_at || 0);

    const timeDiff = timeB - timeA;

    if (Math.abs(timeDiff) < 1000) {
      return (b.id || 0) - (a.id || 0);
    }

    return timeDiff;
  });
};

export const buildAttachmentsFromWebSocketMessage = (messageData) => {
  const attachments = [];
  if (messageData.file) {
    const fileName = messageData.file.split('/').pop() || 'attachment';
    const { type: attachmentType, mediaKind } = resolveMessageAttachmentMeta(
      fileName,
      messageData.media_kind || '',
      messageData.media_mime || ''
    );

    attachments.push({
      name: fileName,
      url: messageData.file,
      size: messageData.media_bytes || 0,
      type: attachmentType,
      mediaKind,
      caption: messageData.caption || '',
      duration: (mediaKind === 'audio' || mediaKind === 'voice') ? (messageData.audio_duration || messageData.duration || null) : null
    });
  }
  return attachments;
};

export const transformWebSocketMessage = (messageData, conversationId) => {
  const attachments = buildAttachmentsFromWebSocketMessage(messageData);
  const replyPreview = messageData.reply_to_preview || null;
  const replyTo =
    messageData.reply_to != null
      ? {
          id: replyPreview?.id ?? messageData.reply_to,
          content: replyPreview?.body || '',
          role: String(replyPreview?.sender_type || '').toLowerCase() === 'customer' ? 'user' : 'assistant',
          timestamp: replyPreview?.created_at || null,
          senderName: replyPreview?.sender_name || null,
          senderId: replyPreview?.sender_id ?? null,
          file: replyPreview?.file || null,
          caption: replyPreview?.caption || '',
          media_kind: replyPreview?.media_kind || ''
        }
      : null;
  
  return {
    id: messageData.id,
    conversation_id: messageData.conversation || conversationId,
    sender_type: messageData.sender_type,
    sender_user: messageData.sender_user,
    sender_contact: messageData.sender_contact,
    sender_name: messageData.sender_name || null,
    content: messageData.body || '',
    caption: messageData.caption || '',
    media_kind: messageData.media_kind || '',
    media_mime: messageData.media_mime || '',
    media_bytes: messageData.media_bytes || null,
    file: messageData.file || null,
    wa_media_id: messageData.wa_media_id || '',
    meta_msg_id: messageData.meta_msg_id || '',
    meta_status: messageData.meta_status || '',
    timestamp: messageData.created_at,
    role: messageData.sender_type === 'Customer' ? 'user' : 'assistant',
    user_id: messageData.conversation || conversationId,
    attachments: attachments,
    reply_to: messageData.reply_to ?? null,
    reply_to_preview: replyPreview,
    replyTo,
    audio: (messageData.media_kind === 'audio' || messageData.media_kind === 'voice') ? {
      url: messageData.file,
      duration: messageData.audio_duration || null
    } : null,
    deleted: messageData.Is_Delete || false,
    edited: messageData.Is_Edit || false
  };
};

export const messageExists = (messages, messageData) => {
  return messages.some(msg => {
    if (msg.id === messageData.id) return true;
    // Check by meta_msg_id if available
    if (messageData.meta_msg_id && msg.meta_msg_id === messageData.meta_msg_id) {
      return true;
    }
    // Fallback check for messages without proper IDs
    if (msg.content === messageData.body && 
        msg.timestamp === messageData.created_at && 
        msg.sender_type === messageData.sender_type) {
      return true;
    }
    return false;
  });
};

export const insertMessageInSortedOrder = (messages, newMessage) => {
  if (!Array.isArray(messages)) return [newMessage];
  if (!newMessage) return messages;
  
  const newTime = new Date(newMessage.timestamp || newMessage.created_at || 0);
  const newId = newMessage.id || 0;
  const sortedMessages = [...messages];
  
  // Find the correct position to insert the new message
  let insertIndex = sortedMessages.length;
  for (let i = 0; i < sortedMessages.length; i++) {
    const existingTime = new Date(sortedMessages[i].timestamp || sortedMessages[i].created_at || 0);
    const existingId = sortedMessages[i].id || 0;
    
    // Compare timestamps first
    const timeDiff = newTime - existingTime;
    
    if (timeDiff < 0) {
      // New message is older, insert here
      insertIndex = i;
      break;
    } else if (Math.abs(timeDiff) < 1000) {
      // Times are very close (within 1 second), use ID as tiebreaker
      if (newId < existingId) {
        insertIndex = i;
        break;
      }
    }
  }
  
  // Insert the new message at the correct position
  sortedMessages.splice(insertIndex, 0, newMessage);
  
  return sortedMessages;
};

export const findMessageIndex = (messages, messageId, metaMsgId = null) => {
  return messages.findIndex(msg => {
    if (msg.id != null && (msg.id == messageId || String(msg.id) === String(messageId))) {
      return true;
    }
    if (msg.meta_msg_id != null && msg.meta_msg_id !== '') {
      if (msg.meta_msg_id == messageId || String(msg.meta_msg_id) === String(messageId)) {
        return true;
      }
      if (metaMsgId && (msg.meta_msg_id == metaMsgId || String(msg.meta_msg_id) === String(metaMsgId))) {
        return true;
      }
    }
    return false;
  });
};

export const extractStatusUpdateData = (statusData) => {
  if (!statusData) return null;
  
  const messageId = statusData.id || statusData.message_id || statusData.meta_msg_id;
  const newStatus = statusData.meta_status || statusData.status;
  const conversationId = statusData.conversation || statusData.conversation_id;
  
  if (!messageId || !newStatus) return null;
  
  return { messageId, newStatus, conversationId, metaMsgId: statusData.meta_msg_id };
};

export const updateMessageInSortedOrder = (messages, updatedMessage) => {
  if (!Array.isArray(messages) || !updatedMessage) return messages;
  
  const messageId = updatedMessage.id;
  const updatedMessages = messages.filter(msg => msg.id !== messageId);
  
  // Insert the updated message in the correct position
  return insertMessageInSortedOrder(updatedMessages, updatedMessage);
};

export const getChatLastMessageTime = (chat) => {
  if (!chat) return new Date(0);
  
  const lastCustomerMsg = new Date(chat.last_customer_msg_at || 0);
  const lastAgentMsg = new Date(chat.last_agent_msg_at || 0);
  const lastTime = new Date(chat.last_time || 0);
  const createdAt = new Date(chat.created_at || 0);
  
  // Return the most recent timestamp
  return new Date(Math.max(lastCustomerMsg, lastAgentMsg, lastTime, createdAt));
};

export const shouldMessageHavePolygon = (message, messages, messageIndex, isUnread) => {
  if (!message || !Array.isArray(messages) || messageIndex < 0) return false;
  
  // If message is unread, it should have polygon
  if (isUnread) return true;
  
  // If this is the first message in the conversation, it should have polygon
  if (messageIndex === 0) return true;
  
  // Get the previous message
  const previousMessage = messages[messageIndex - 1];
  if (!previousMessage) return true;
  
  // If the previous message is from a different sender, this is the first message in a new thread
  // So it should have polygon
  if (previousMessage.role !== message.role) return true;
  
  // Otherwise, no polygon (continuation of same sender's messages)
  return false;
};

export const mergeAndDeduplicateMessages = (existingMessages = [], newMessages = []) => {
  const messageMap = new Map();
  
  // Add existing messages
  existingMessages.forEach(msg => {
    if (msg.id) {
      messageMap.set(msg.id, msg);
    } else {
      // For messages without ID, use a composite key
      const key = `${msg.timestamp}-${msg.content?.substring(0, 50) || ''}`;
      messageMap.set(key, msg);
    }
  });
  
  // Add new messages (overwrites duplicates)
  newMessages.forEach(msg => {
    if (msg.id) {
      messageMap.set(msg.id, msg);
    } else {
      const key = `${msg.timestamp}-${msg.content?.substring(0, 50) || ''}`;
      messageMap.set(key, msg);
    }
  });
  
  // Convert to array and sort by timestamp (oldest first)
  const merged = Array.from(messageMap.values());
  return sortMessagesByTime(merged);
};

export const shouldShowAvatar = (message, previousMessage) => {
  // Never show avatar for Customer messages
  if (message.sender_type === 'Customer') {
    return false;
  }
  
  // Only show avatar for Agent and Bot messages
  if (message.sender_type !== 'Agent' && message.sender_type !== 'Bot') {
    return false;
  }
  
  // If this is the first message, show avatar
  if (!previousMessage) {
    return true;
  }
  
  // If previous message was from Customer, show avatar (new sender thread)
  if (previousMessage.sender_type === 'Customer') {
    return true;
  }
  
  // If previous message was from different sender, show avatar
  if (previousMessage.sender_type !== message.sender_type) {
    return true;
  }
  
  // If more than 5 minutes passed, show avatar
  const timeDiff = new Date(message.timestamp) - new Date(previousMessage.timestamp);
  if (timeDiff > 5 * 60 * 1000) {
    return true;
  }
  
  return false;
};

export const isMessageFromUser = (message) => {
  return message.role !== 'user' && message.sender_type !== 'Customer';
};
